/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {
  Train,
  TrainType
}                          from './train';
import { Track }           from './track';
import { TrainClass }      from './train.class';
import { Period }          from "@/util/period";

/**
 *
 * @class ScheduledTrain
 *
 * @brief variety that schedules trains on certain days of the week with some randomization
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class ScheduledTrain extends Train {

  /** @var _days the days of the week when this schedule applies */
  public         _days: string = '';

  /** @var _percentEarly uniform chance of being early */
  public _percentEarly: number = 0;

  /** @var _stdDevEarly spread when it leaves early */
  public  _stdDevEarly: number = 0;

  /** @var _stdDevLate spread when it leaves late */
  public   _stdDevLate: number = 0;

  /** @var _seed randomization seed */
  public         _seed: number = 0;

  /** @var _departure (seconds) when train departs starting terminal/junction */
  public    _departure: number = 0;

  /** @brief standard constructor */
  constructor(          id: number,
                      name: string,
                     paths: (Track|null)[][],
                trainClass: TrainClass|null,
                   tonnage: number,
                    length: number,
                  priority: number,
                  overtake: number,
                      days: string,
              percentEarly: number,
               stdDevEarly: number,
                stdDevLate: number,
                      seed: number,
                 departure: number,
                       nth: number) {

    super(id, name, paths, trainClass, tonnage, length, priority, overtake, nth);

    this._type         = TrainType.SCHEDULED;
    this._days         = days;
    this._percentEarly = percentEarly;
    this._stdDevEarly  = stdDevEarly;
    this._stdDevLate   = stdDevLate;
    this._seed         = seed;
    this._departure    = departure;
  }

  /** @brief helper to make a new instance of this template */
  public clone(): Train {

    let newTrain: ScheduledTrain = new ScheduledTrain(
      this._id,
      this._name,
      this._paths,
      this._tc,
      this._tonnage,
      this._length,
      this._priority,
      this._overtake,
      this._days,
      this._percentEarly,
      this._stdDevEarly,
      this._stdDevLate,
      this._seed,
      this._departure,
      -1
    );

    newTrain._type      = this._type;
    newTrain._tag       = this._tag;
    newTrain._stops     = this._stops;
    newTrain._workFlags = this._workFlags;

    return newTrain;

  }

  /** when editing fails because the value given was entirely unusable, fill in a default but still bad value */
  public defaultBadValue(field:  string): void {

    /*
     * this method is required because they may give a value like "undefined" which can't
     * match any of our types, it can't be "set" as the value.  In cases like that, we fill
     * in a typed value that does match the type of the field, but It's still a bad value,
     * to reflect that the edit is invalid.
     *
     */

    switch (field) {

      case 'days':         this._days         = ''; return ;
      case 'percentearly': this._percentEarly = -1; return ;
      case 'early':        this._stdDevEarly  = -1; return ;
      case 'late':         this._stdDevLate   = -1; return ;
      case 'departure':    this._departure    = -1; return ;
      case 'seed':         this._seed         = -1; return ;
    }

    super.defaultBadValue(field);
  }

  /* before editing, translate/cast the value we're about to set (if required) */
  public castValue(field: string, value: any): any {

    /* if they give us a Period instead of seconds for 'departure'; just downcast to seconds */

    if ((field === 'departure') && (value instanceof Period)) {

      return (value as Period).asSeconds();
    }

    return super.castValue(field, value);
  }

  /** edit this field on this train (if applicable), return true if edit succeeded */
  public edit(field: string, value: any): boolean {

    const param:  {field: string, value: any} = this.prepare(field, value);

    switch (param.field) {

      case 'days':         this._days         = param.value as string;     return this.validate();
      case 'percentearly': this._percentEarly = param.value as number;     return this.validate();
      case 'early':        this._stdDevEarly  = param.value as number;     return this.validate();
      case 'late':         this._stdDevLate   = param.value as number;     return this.validate();
      case 'departure':    this._departure    = param.value as number;     return this.validate();
      case 'seed':         this._seed         = param.value as number;     return this.validate();

    }

    return super.edit(field, value);
  }

  /**
   * sometimes errors, are not absolutely critical errors, they are really just
   * warnings, or at least a kind of error we consider suppressible. Each kind of
   * train generates its own errors through its own validation, so we must allow
   * each one to define its own kind of suppression.
   *
   * @param field string the field we had an error on
   * @param error string the error for that field.
   *
   * @return boolean true means the error can be suppressed or considered a warning.
   */
  public suppressible(field: string, error: string): boolean {

    if (error == '') {

      /* it's not an error, so you can't suppress it :) */

      return false;
    }

    if (field == 'days') {

      if (/At\s+least\s+one\s+day\s+must\s+be\s+scheduled/.test(error)) {

        /* if they don't give "Days of Week"...its not great, but we're not going to block them */

        return true;
      }
    }

    /* by default, errors are errors :) */

    return false;
  }

  /** check if this value *would* be valid for this field */
  public checkValue(field: string, value: any): string {

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      return '';
    }

    switch (f) {

      case 'seed': {

        if (!this.isPositiveNumber(value)) {

            return `Random seed can not be negative number.`;
          }
        }
        break;

      case 'departure': {


        if (!this.isPositiveNumber(value)) {

            return `Departure can not be negative number.`;
          }
        }
        break;

      case 'days': {

          if ((typeof value !== 'string') || (value === '')) {

            return `At least one day must be scheduled.`;
          }
        }
        break;

      case 'percentearly': {

          if (!this.isPositiveNumber(value)) {

            return `Percent early can not be negative number`;
          }
        }
        break;

      case 'early': {

        if (!this.isPositiveNumber(value)) {

            return `Arrival deviation can not be negative number.`;
          }
        }
        break;

      case 'late': {

        if (!this.isPositiveNumber(value)) {

            return `Departure deviation can not be negative number.`;
          }
        }
        break;

    }

    /* no obvious error */

    return super.checkValue(f, value);
  }

  /** reset the list of invalid fields, based on current values */
  public validate(): boolean {

    super.validate();

    /* sub-class validation ... */

    return this.validateValues([
      ['seed',         this._seed,           'seed'],
      ['departure',    this._departure,      'departure'],
      ['days',         this._days,           'days'],
      ['percentearly', this._percentEarly,   'percentearly'],
      ['early',        this._stdDevEarly,    'early'],
      ['late',         this._stdDevLate,     'late']
    ]);
  }
}
